import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import axios from 'axios';

// Helper function to get the current user data from context
const getUserData = () => {
  // Try to get user data from localStorage if available
  try {
    const userData = localStorage.getItem('userData');
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('Error getting user data:', error);
    return null;
  }
};

// Helper to create headers with authorization and user data
const createHeaders = () => {
  const headers = { 
    Authorization: `Bearer ${process.env.REACT_APP_ACCESS_TOKEN}` 
  };
  
  const userData = getUserData();
  if (userData) {
    // Add user data to headers
    headers['X-User-Data'] = JSON.stringify(userData);
  } else {
    console.warn('No user data available in localStorage - requests may not be properly filtered');
  }
  
  return headers;
};

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function getLineItems(query = ''){
  return axios.get(process.env.REACT_APP_API_URL + `/api/LineItems${query}`, { 
    headers: createHeaders() 
  });
}

export function getShipments(query){
  return axios.get(process.env.REACT_APP_API_URL + `/api/Shipments${query}`, { 
    headers: createHeaders() 
  });
}

export function getPeopleByEmail(email) {
  const query = `?email=${encodeURIComponent(email.trim())}`;
  return axios.get(process.env.REACT_APP_API_URL + `/api/Peoples${query}`, {
    headers: createHeaders()
  });
}

export function getPeopleByFireauthId(fireauthId) {
  const query = `?fireauth_id=${encodeURIComponent(fireauthId)}`;
  return axios.get(process.env.REACT_APP_API_URL + `/api/Peoples${query}`, {
    headers: createHeaders()
  });
}

export function getPeople(query = '') {
  return axios.get(process.env.REACT_APP_API_URL + `/api/Peoples${query}`, {
    headers: createHeaders()
  });
}
export function deletePeople(people) {
  return axios.post(
    process.env.REACT_APP_API_URL + '/api/Peoples/delete',
    people,
    { headers: createHeaders() }
  );
}

export function createPeople(people) {
  return axios.post(
    process.env.REACT_APP_API_URL + '/api/Peoples',
    people,
    { headers: createHeaders() }
  );
}
/**
 * Update a person's fireauth_id in the People table
 * @param {Object} person - The person object to update
 * @param {string} fireAuthId - The Firebase Auth ID to set
 * @returns {Promise} - The axios response
 */
export function updatePersonFireAuthId(person, fireAuthId) {
  // Create an updated person object with the fireauth_id
  const updatedPerson = {
    ...person,
    fireauth_id: fireAuthId
  };
  
  // Send the update request
  return axios.post(
    process.env.REACT_APP_API_URL + '/api/Peoples',
    [updatedPerson], // API expects an array of people
    { headers: createHeaders() }
  );
}

export function createShipment(shipmentData) {
  return axios.post(
    process.env.REACT_APP_API_URL + '/api/Shipments',
    shipmentData,
    { headers: createHeaders() }
  );
}

export function createParts(partsData) {
  return axios.post(
    process.env.REACT_APP_API_URL + '/api/Parts',
    partsData,
    { headers: createHeaders() }
  );
}

export function updateParts(partsData) {
  return axios.put(
    process.env.REACT_APP_API_URL + '/api/Parts',
    partsData,
    { headers: createHeaders() }
  );
}

export function deletePart(partData) {
  return axios.post(
    process.env.REACT_APP_API_URL + '/api/Parts/delete',
    partData,
    { headers: createHeaders() }
  );
}

export function deleteLineItem(lineItemData) {
  return axios.post(
    process.env.REACT_APP_API_URL + '/api/LineItems/delete',
    lineItemData,
    { headers: createHeaders() }
  );
}

export function updateLineItem(lineItem,query){
  return axios.post(process.env.REACT_APP_API_URL + `/api/LineItems?${query}`, lineItem, { headers: createHeaders() });
}

export function updateShipment(shipmentData, shipmentId) {
  const query = shipmentId ? `?id=${encodeURIComponent(shipmentId)}` : '';
  return axios.put(
    process.env.REACT_APP_API_URL + `/api/Shipments${query}`,
    [shipmentData], // API expects an array of shipments
    { headers: createHeaders() }
  );
}

export function getDocuments(query){
  return axios.get(process.env.REACT_APP_API_URL + `/api/Files${query}`, { headers: createHeaders() });
}

export function getParts(lineItem, shipment) {
  const query = `?po=${encodeURIComponent(lineItem)}&shipment=${encodeURIComponent(shipment)}`;
  return axios.get(process.env.REACT_APP_API_URL + `/api/Parts/getParts${query}`, { headers: createHeaders() });
}

export function uploadFiles(formData){
  return axios.post(process.env.REACT_APP_API_URL + '/api/Files', formData, { 
      headers: { 
        'Authorization': `Bearer ${process.env.REACT_APP_ACCESS_TOKEN}`,
        'Content-Type': 'multipart/form-data',  // Important for file uploads
        ...createHeaders()
      } 
    });
}

  // Function to format file size
export function formatFileSize(size) {
    const sizeNum = parseInt(size);
    if (sizeNum < 1024) return `${sizeNum} B`;
    if (sizeNum < 1024 * 1024) return `${(sizeNum / 1024).toFixed(1)} KB`;
    return `${(sizeNum / (1024 * 1024)).toFixed(1)} MB`;
};


export function getDocumentTypeIcon(type){
  switch (type?.toLowerCase()) {
    case 'airway bill':
      return 'AWB';
    case 'bill of lading':
      return 'BOL';
    case 'commercial invoice':
      return 'CI';
    case 'house bill':
      return 'HB';
    case 'manifest':
      return 'MF';
    case 'master bill':
      return 'MB';
    case 'packing list':
      return 'PL';
    case 'proof of delivery':
      return 'POD';
    case 'other':
      return 'DOC';
    default:
      return 'DOC';
  }
};